import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, resolveImg, track } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { compositeMockup, publishShareCard } from "@/lib/studioCanvas";
import ShareDialog from "@/components/ShareDialog";
import { ImageEditor } from "@/components/ImageEditor";
import { Seo } from "@/components/Seo";
import {
  Upload, Library as LibIcon, ShoppingBag, Share2, RotateCw, Maximize2,
  Shirt, Save, X, Sparkles, User, Loader2, FlipHorizontal2, Scissors,
} from "lucide-react";

const DEFAULT_PLACEMENT = { front: { x: 0.5, y: 0.42, scale: 0.3, rotation: 0 }, back: { x: 0.5, y: 0.40, scale: 0.32, rotation: 0 } };

export default function Studio() {
  const { addCustom } = useCart();
  const { user } = useAuth();

  const [blanks, setBlanks] = useState([]);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [surcharge, setSurcharge] = useState(5);
  const [backSurcharge, setBackSurcharge] = useState(6);

  const [blankId, setBlankId] = useState(null);
  const [colorHex, setColorHex] = useState("#16233f");
  const [size, setSize] = useState("M");
  const [side, setSide] = useState("front");                 // currently edited side
  const [designs, setDesigns] = useState({ front: null, back: null }); // each {design_id,url,src}
  const [placements, setPlacements] = useState(DEFAULT_PLACEMENT);
  const [bundleBack, setBundleBack] = useState(true);        // front+back bundle (upsell ON by default)
  const [mode, setMode] = useState("flat");
  const [personas, setPersonas] = useState([]);
  const [personaId, setPersonaId] = useState(null);
  const [name, setName] = useState("My Custom Design");

  const [picker, setPicker] = useState(false);
  const [library, setLibrary] = useState([]);
  const [busy, setBusy] = useState(false);
  const [share, setShare] = useState({ open: false, dataUrl: null, url: null });
  const [editor, setEditor] = useState({ open: false, src: null });

  const mockupRef = useRef(null);
  const dragging = useRef(false);
  const fileRef = useRef(null);
  const [params] = useSearchParams();
  const preloaded = useRef(false);

  // Deep-link from "Shop this look" — preload a product print as the front design
  useEffect(() => {
    const printUrl = params.get("print");
    if (!printUrl || preloaded.current) return;
    preloaded.current = true;
    const nm = params.get("name") || "Featured Design";
    (async () => {
      setBusy(true);
      try {
        const blob = await (await fetch(resolveImg(printUrl))).blob();
        const dataUrl = await new Promise((res, rej) => {
          const fr = new FileReader();
          fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(blob);
        });
        const r = await api.post("/studio/designs", { name: nm, image_base64: dataUrl, source: "product-import" });
        setDesigns((d) => ({ ...d, front: { design_id: r.data.design_id, url: r.data.url, src: resolveImg(r.data.url) } }));
        setName(nm);
        track("studio_load", { design_id: r.data.design_id });
        toast.success(`${nm} loaded — choose your garment & color`);
      } catch { toast.error("Could not preload that design"); }
      finally { setBusy(false); }
    })();
  }, [params]);

  useEffect(() => {
    api.get("/shop/blanks").then((r) => {
      setBlanks(r.data.blanks); setColors(r.data.colors); setSizes(r.data.sizes);
      setSurcharge(r.data.surcharge); setBackSurcharge(r.data.back_surcharge ?? 6);
      setBlankId(r.data.blanks[0]?.blank_id);
    });
    api.get("/studio/personas").then((r) => { setPersonas(r.data); setPersonaId(r.data[0]?.id); });
  }, []);

  const blank = blanks.find((b) => b.blank_id === blankId);
  const persona = personas.find((p) => p.id === personaId);

  // The back is included when bundle is on and there's a front (or dedicated back) design.
  const effectiveBack = bundleBack ? (designs.back || designs.front) : null;
  const hasBack = !!effectiveBack;
  const price = blank ? blank.base_price + surcharge + (hasBack ? backSurcharge : 0) : 0;

  const loadLibrary = useCallback(() => {
    api.get("/studio/designs").then((r) => setLibrary(r.data)).catch(() => {});
  }, []);
  const openPicker = () => { loadLibrary(); setPicker(true); };

  const setDesignForSide = (val) => setDesigns((d) => ({ ...d, [side]: val }));

  // open the image editor on the current side's artwork (front fallback when bundling)
  const openEditor = () => {
    const d = designs[side] || (side === "back" && bundleBack ? designs.front : null);
    if (!d) { toast.error("Add an image to this side first"); return; }
    setEditor({ open: true, src: d.src });
  };
  const onEditApply = async (dataUrl) => {
    setBusy(true);
    try {
      const r = await api.post("/studio/designs", { name: `${name} (edited)`, image_base64: dataUrl, source: "edited" });
      setDesignForSide({ design_id: r.data.design_id, url: r.data.url, src: resolveImg(r.data.url) });
      track("studio_edit", { side });
      toast.success(`Edited artwork applied to ${side}`);
    } catch (e) { console.error("edit save failed", e); toast.error("Could not save edited image"); }
    finally { setBusy(false); setEditor({ open: false, src: null }); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", file.name);
      const res = await api.post("/studio/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDesignForSide({ design_id: res.data.design_id, url: res.data.url, src: resolveImg(res.data.url) });
      toast.success(`Artwork uploaded to ${side}`);
      setPicker(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  };

  const pickFromLibrary = async (item) => {
    setBusy(true);
    try {
      if (item.kind === "design") {
        setDesignForSide({ design_id: item.id, url: item.url, src: resolveImg(item.url) });
      } else {
        const res = await api.post("/studio/designs", { name: item.name, image_base64: item.url, source: "ai-import" });
        setDesignForSide({ design_id: res.data.design_id, url: res.data.url, src: resolveImg(res.data.url) });
      }
      setPicker(false);
    } catch (err) { toast.error("Could not load design"); }
    finally { setBusy(false); }
  };

  // Currently displayed design on the canvas (back falls back to front preview when bundling)
  const sideDesign = designs[side] || (side === "back" && bundleBack ? designs.front : null);
  const editable = !!designs[side];               // only a dedicated side design is draggable
  const curPlacement = designs[side] ? placements[side] : (side === "back" ? placements.front : placements[side]);

  // drag the design across the mockup
  const startDrag = (e) => { if (!editable) return; e.preventDefault(); dragging.current = true; };
  const onMove = (e) => {
    if (!dragging.current || !mockupRef.current) return;
    const r = mockupRef.current.getBoundingClientRect();
    const cx = (e.clientX ?? e.touches?.[0]?.clientX) - r.left;
    const cy = (e.clientY ?? e.touches?.[0]?.clientY) - r.top;
    setPlacements((p) => ({ ...p, [side]: { ...p[side], x: Math.min(0.95, Math.max(0.05, cx / r.width)), y: Math.min(0.95, Math.max(0.05, cy / r.height)) } }));
  };
  const stopDrag = () => { dragging.current = false; };

  const garmentSrc = blank ? blank.image : null;

  const buildComposite = async (forSide, size_ = 800) => {
    const dz = designs[forSide] || (forSide === "back" && bundleBack ? designs.front : null);
    const pl = designs[forSide] ? placements[forSide] : (forSide === "back" ? placements.front : placements[forSide]);
    if (mode === "persona" && persona) {
      const pSrc = forSide === "back" && persona.back_image ? persona.back_image : persona.image;
      return compositeMockup({ garmentSrc: pSrc, colorHex: null, designSrc: dz?.src, placement: pl, size: size_, bg: "#070b14" });
    }
    return compositeMockup({ garmentSrc, colorHex, designSrc: dz?.src, placement: pl, size: size_, bg: "#0a0f1c" });
  };

  const saveToLibrary = async () => {
    if (!designs.front) { toast.error("Add a front design first"); return; }
    setBusy(true);
    try {
      const dataUrl = await buildComposite(side, 900);
      await api.post("/studio/designs", { name, image_base64: dataUrl, source: "composite" });
      toast.success("Saved to your library");
    } catch (err) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const addToCart = async () => {
    if (!designs.front) { toast.error("Add a front design first"); return; }
    if (!blank) return;
    setBusy(true);
    try {
      const frontThumb = await buildComposite("front", 500);
      const colorName = colors.find((c) => c.hex === colorHex)?.name || "Custom";
      const backThumb = hasBack ? await buildComposite("back", 500) : null;
      const backPlacement = designs.back ? placements.back : placements.front;
      addCustom({
        blank_id: blank.blank_id, design_id: designs.front.design_id, design_url: designs.front.url,
        name, type: blank.type, price, image: frontThumb, image_back: backThumb, accent: "#00E5FF",
        color: colorName, color_hex: colorHex, size, qty: 1,
        placement: placements.front,
        design_id_back: hasBack ? effectiveBack.design_id : null,
        design_url_back: hasBack ? effectiveBack.url : null,
        placement_back: hasBack ? backPlacement : null,
      });
      toast.success(`${name} added to cart · $${price.toFixed(2)}${hasBack ? " (front+back)" : ""}`);
    } catch (err) { toast.error("Could not add to cart"); }
    finally { setBusy(false); }
  };

  const openShare = async () => {
    if (!designs.front) { toast.error("Add a design first"); return; }
    setBusy(true);
    try {
      const dataUrl = await buildComposite(side, 1000);
      const { url, nativeShared } = await publishShareCard(dataUrl, `Check out my custom ${blank?.type} on AETHER`);
      if (!nativeShared) setShare({ open: true, dataUrl, url });
      else toast.success("Shared!");
    } catch (err) { toast.error("Share failed"); }
    finally { setBusy(false); }
  };

  const designStyle = {
    position: "absolute",
    left: `${curPlacement.x * 100}%`, top: `${curPlacement.y * 100}%`,
    width: `${curPlacement.scale * 100}%`,
    transform: `translate(-50%, -50%) rotate(${curPlacement.rotation}deg)`,
    cursor: editable ? "grab" : "default", touchAction: "none", userSelect: "none",
    opacity: editable ? 1 : 0.55,
  };

  return (
    <div className="min-h-screen bg-app relative pt-10 pb-28" data-testid="studio-page"
         onPointerMove={onMove} onPointerUp={stopDrag} onPointerLeave={stopDrag}>
      <Seo title="Design Studio" description="Create custom sci-fi apparel — mask, cut-out, frame, add text & stickers, then print front & back." path="/studio" noindex />
      <div className="absolute inset-0 hud-grid opacity-15" />
      <div className="relative mx-auto max-w-7xl px-5">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
          <div>
            <div className="font-mono text-[11px] tracking-[0.4em] text-cyan/70">CREATION → COMMERCE</div>
            <h1 className="font-head text-4xl font-bold uppercase mt-2">Design <span className="text-cyan text-glow-cyan">Studio</span></h1>
          </div>
          <Link to="/library" data-testid="studio-library-link" className="btn-hud flex items-center gap-2 border border-cyan/40 text-cyan px-4 py-2 text-xs hover:bg-cyan hover:text-app">
            <LibIcon className="w-4 h-4" /> My Library
          </Link>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* LEFT — garment + color + size */}
          <div className="lg:col-span-3 space-y-5">
            <Panel title="GARMENT" icon={Shirt}>
              <div className="grid grid-cols-2 gap-2" data-testid="blank-selector">
                {blanks.map((b) => (
                  <button key={b.blank_id} onClick={() => setBlankId(b.blank_id)} data-testid={`blank-${b.blank_id}`}
                    className={`p-2 border text-left transition-all ${blankId === b.blank_id ? "border-cyan box-glow-cyan" : "border-white/10 hover:border-cyan/40"}`}>
                    <img src={b.image} alt={b.name} className="w-full h-16 object-contain" />
                    <div className="font-mono text-[9px] tracking-wide text-slate-300 mt-1 truncate">{b.name}</div>
                    <div className="font-mono text-[9px] text-cyan">${b.base_price.toFixed(0)}+</div>
                  </button>
                ))}
              </div>
            </Panel>

            <Panel title="FABRIC COLOR">
              <div className="flex flex-wrap gap-2" data-testid="color-selector">
                {colors.map((c) => (
                  <button key={c.id} onClick={() => setColorHex(c.hex)} title={c.name} data-testid={`color-${c.id}`}
                    className={`w-9 h-9 rounded-full border-2 transition-all ${colorHex === c.hex ? "border-cyan scale-110 box-glow-cyan" : "border-white/20"}`}
                    style={{ background: c.hex }} />
                ))}
              </div>
            </Panel>

            <Panel title="SIZE">
              <div className="flex flex-wrap gap-2" data-testid="size-selector">
                {sizes.map((s) => (
                  <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                    className={`w-10 h-9 font-mono text-xs border transition-all ${size === s ? "border-cyan text-cyan box-glow-cyan" : "border-white/15 text-slate-400 hover:border-cyan/40"}`}>{s}</button>
                ))}
              </div>
            </Panel>
          </div>

          {/* CENTER — live mockup */}
          <div className="lg:col-span-6">
            <div className="glass-strong clip-chamfer brackets p-4">
              <div className="flex items-center justify-center gap-2 mb-3">
                {["flat", "persona"].map((m) => (
                  <button key={m} onClick={() => setMode(m)} data-testid={`mode-${m}`}
                    className={`font-mono text-[10px] tracking-widest px-4 py-1.5 border transition-all ${mode === m ? "border-cyan text-cyan" : "border-white/10 text-slate-400"}`}>
                    {m === "flat" ? <><Shirt className="inline w-3 h-3 mr-1" /> FLAT MOCKUP</> : <><User className="inline w-3 h-3 mr-1" /> ON PERSONA</>}
                  </button>
                ))}
              </div>

              {/* Front / Back side toggle (flat mockup + personas with a rear render) */}
              {(mode === "flat" || (mode === "persona" && persona?.back_image)) && (
                <div className="flex items-center justify-center gap-2 mb-3" data-testid="side-toggle">
                  {["front", "back"].map((sd) => (
                    <button key={sd} onClick={() => setSide(sd)} data-testid={`side-${sd}`}
                      className={`font-mono text-[10px] tracking-widest px-5 py-1.5 border transition-all ${side === sd ? "border-cyan text-cyan box-glow-cyan" : "border-white/10 text-slate-400"}`}>
                      <FlipHorizontal2 className="inline w-3 h-3 mr-1" /> {sd.toUpperCase()}
                      {sd === "back" && designs.back && <span className="ml-1 text-cyan">●</span>}
                    </button>
                  ))}
                </div>
              )}

              {mode === "persona" && (
                <div className="flex gap-2 justify-center mb-3 flex-wrap" data-testid="persona-selector">
                  {personas.map((p) => (
                    <button key={p.id} onClick={() => setPersonaId(p.id)} data-testid={`persona-${p.id}`}
                      className={`font-mono text-[9px] tracking-widest px-3 py-1.5 border transition-all ${personaId === p.id ? "border-cyan text-cyan" : "border-white/10 text-slate-400"}`}>
                      {p.title}
                    </button>
                  ))}
                </div>
              )}

              <div ref={mockupRef} className="relative mx-auto w-full max-w-md aspect-square overflow-hidden bg-[#0a0f1c]" data-testid="mockup-canvas">
                {mode === "flat" && garmentSrc && (
                  <>
                    <img src={garmentSrc} alt="garment" className="absolute inset-0 w-full h-full object-contain" draggable={false} style={side === "back" ? { transform: "scaleX(-1)" } : undefined} />
                    {colorHex.toLowerCase() !== "#b8bcc4" && (
                      <div className="absolute inset-0" style={{
                        WebkitMaskImage: `url(${garmentSrc})`, maskImage: `url(${garmentSrc})`,
                        WebkitMaskSize: "contain", maskSize: "contain",
                        WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
                        WebkitMaskPosition: "center", maskPosition: "center",
                        background: colorHex, mixBlendMode: "multiply",
                      }} />
                    )}
                    <div className="absolute top-2 left-2 font-mono text-[8px] tracking-widest px-2 py-0.5 border border-cyan/30 text-cyan/80 bg-app/60">{side.toUpperCase()} VIEW</div>
                  </>
                )}
                {mode === "persona" && persona && (
                  <img src={side === "back" && persona.back_image ? persona.back_image : persona.image} alt={persona.title} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
                )}
                {sideDesign ? (
                  <img src={sideDesign.src} alt="design" style={designStyle} draggable={false}
                       onPointerDown={startDrag} data-testid="design-layer" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="text-center text-slate-500">
                      <Sparkles className="w-7 h-7 mx-auto mb-2 text-cyan/40" />
                      <div className="font-mono text-[10px] tracking-widest">{side === "front" ? "ADD A DESIGN TO BEGIN" : "ADD A BACK DESIGN (OPTIONAL)"}</div>
                    </div>
                  </div>
                )}
                {side === "back" && !designs.back && designs.front && bundleBack && (
                  <div className="absolute bottom-2 inset-x-2 text-center font-mono text-[8px] tracking-widest text-amber/80 bg-app/60 py-1" data-testid="back-mirror-note">
                    FRONT DESIGN PRINTED ON BACK — UPLOAD ARTWORK TO CUSTOMIZE
                  </div>
                )}
              </div>

              {/* placement controls (only for an editable side design) */}
              {editable && (
                <div className="grid grid-cols-2 gap-4 mt-4 max-w-md mx-auto">
                  <label className="block">
                    <span className="flex items-center gap-1 font-mono text-[9px] tracking-widest text-slate-400 mb-1"><Maximize2 className="w-3 h-3" /> SCALE</span>
                    <input type="range" min="0.12" max="0.6" step="0.01" value={curPlacement.scale} data-testid="scale-slider"
                      onChange={(e) => setPlacements((p) => ({ ...p, [side]: { ...p[side], scale: parseFloat(e.target.value) } }))} className="w-full accent-cyan" />
                  </label>
                  <label className="block">
                    <span className="flex items-center gap-1 font-mono text-[9px] tracking-widest text-slate-400 mb-1"><RotateCw className="w-3 h-3" /> ROTATE</span>
                    <input type="range" min="-180" max="180" step="1" value={curPlacement.rotation} data-testid="rotate-slider"
                      onChange={(e) => setPlacements((p) => ({ ...p, [side]: { ...p[side], rotation: parseInt(e.target.value) } }))} className="w-full accent-cyan" />
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — actions */}
          <div className="lg:col-span-3 space-y-4">
            <Panel title={`ARTWORK · ${side.toUpperCase()}`}>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => fileRef.current?.click()} data-testid="upload-btn"
                  className="flex flex-col items-center gap-1 p-3 border border-white/10 hover:border-cyan transition-colors">
                  <Upload className="w-4 h-4 text-cyan" /><span className="font-mono text-[9px] tracking-widest text-slate-300">UPLOAD</span>
                </button>
                <button onClick={openPicker} data-testid="pick-library-btn"
                  className="flex flex-col items-center gap-1 p-3 border border-white/10 hover:border-cyan transition-colors">
                  <LibIcon className="w-4 h-4 text-cyan" /><span className="font-mono text-[9px] tracking-widest text-slate-300">LIBRARY</span>
                </button>
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onUpload} data-testid="file-input" />
              <button onClick={openEditor} disabled={busy || !(designs[side] || (side === "back" && bundleBack && designs.front))} data-testid="edit-image-btn"
                className="mt-2 w-full flex items-center justify-center gap-2 p-2.5 border border-amber/40 text-amber hover:bg-amber hover:text-app transition-colors disabled:opacity-40">
                <Scissors className="w-4 h-4" /><span className="font-mono text-[9px] tracking-widest">EDIT · MASK · CUT-OUT</span>
              </button>
              {designs.front && <div className="mt-2 font-mono text-[9px] tracking-widest text-cyan/70">✓ FRONT LOADED{designs.back ? " · BACK LOADED" : ""}</div>}
            </Panel>

            {/* Front + Back bundle upsell */}
            <Panel title="PRINT BUNDLE">
              <button
                onClick={() => setBundleBack((v) => !v)}
                data-testid="bundle-toggle"
                role="switch"
                aria-checked={bundleBack}
                className={`w-full flex items-center justify-between p-3 border transition-all ${bundleBack ? "border-cyan box-glow-cyan" : "border-white/15"}`}
              >
                <div className="text-left">
                  <div className="font-head text-xs uppercase tracking-wide text-slate-100">Front + Back</div>
                  <div className="font-mono text-[9px] text-slate-500">Add a back print · +${backSurcharge.toFixed(2)}</div>
                </div>
                <div className={`w-10 h-5 rounded-full p-0.5 transition-colors ${bundleBack ? "bg-cyan" : "bg-white/15"}`}>
                  <div className={`w-4 h-4 rounded-full bg-app transition-transform ${bundleBack ? "translate-x-5" : ""}`} />
                </div>
              </button>
              <div className="font-mono text-[9px] text-slate-500 mt-2">
                {bundleBack
                  ? (designs.back ? "Custom back artwork applied." : "Front design prints on the back by default — switch to BACK to customize.")
                  : "Front print only."}
              </div>
            </Panel>

            <Panel title="DETAILS">
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="design-name-input"
                className="w-full bg-cyan/5 border-b border-cyan/25 focus:border-cyan text-slate-100 px-3 py-2 font-mono text-sm outline-none" />
              <div className="flex items-center justify-between mt-4">
                <span className="font-mono text-[10px] tracking-widest text-slate-400">PRICE</span>
                <span className="font-head text-2xl font-bold text-cyan" data-testid="studio-price">${price.toFixed(2)}</span>
              </div>
              <div className="font-mono text-[9px] text-slate-500">
                base ${blank?.base_price.toFixed(2)} + ${surcharge.toFixed(2)} print{hasBack ? ` + $${backSurcharge.toFixed(2)} back` : ""}
              </div>
            </Panel>

            <button onClick={addToCart} disabled={busy || !designs.front} data-testid="studio-add-cart"
              className="btn-hud w-full flex items-center justify-center gap-2 border border-cyan text-cyan py-3 text-sm box-glow-cyan hover:bg-cyan hover:text-app disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />} Add to Cart
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={saveToLibrary} disabled={busy || !designs.front} data-testid="studio-save"
                className="btn-hud flex items-center justify-center gap-2 border border-white/15 text-slate-300 py-2.5 text-xs hover:border-cyan hover:text-cyan disabled:opacity-40">
                <Save className="w-3.5 h-3.5" /> Save
              </button>
              <button onClick={openShare} disabled={busy || !designs.front} data-testid="studio-share"
                className="btn-hud flex items-center justify-center gap-2 border border-amber/40 text-amber py-2.5 text-xs hover:bg-amber hover:text-app disabled:opacity-40">
                <Share2 className="w-3.5 h-3.5" /> Share
              </button>
            </div>
            <div className="font-mono text-[9px] tracking-widest text-slate-500 text-center">PRINTED ON-DEMAND · NICOSIA, CYPRUS</div>
          </div>
        </div>
      </div>

      {/* Library picker modal */}
      {picker && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-app/80 backdrop-blur-sm p-5" data-testid="design-picker" onClick={() => setPicker(false)}>
          <div className="relative w-full max-w-3xl max-h-[80vh] overflow-y-auto glass-strong clip-chamfer brackets p-6" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPicker(false)} className="absolute top-3 right-3 text-slate-400 hover:text-cyan"><X className="w-5 h-5" /></button>
            <div className="font-mono text-[10px] tracking-[0.3em] text-cyan/70 mb-4">SELECT FROM YOUR LIBRARY · {side.toUpperCase()}</div>
            <button onClick={() => fileRef.current?.click()} className="w-full mb-4 flex items-center justify-center gap-2 p-3 border border-dashed border-cyan/40 text-cyan hover:bg-cyan/5 font-mono text-[10px] tracking-widest">
              <Upload className="w-4 h-4" /> UPLOAD NEW IMAGE
            </button>
            {library.length === 0 ? (
              <div className="text-center py-10 text-slate-500 font-mono text-xs">No designs yet — upload one or generate art in the Simulation.</div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {library.map((it) => (
                  <button key={it.id} onClick={() => pickFromLibrary(it)} data-testid={`library-pick-${it.id}`}
                    className="group relative aspect-square border border-white/10 hover:border-cyan overflow-hidden bg-app">
                    <img src={resolveImg(it.url)} alt={it.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
                    <span className="absolute bottom-0 inset-x-0 bg-app/80 font-mono text-[8px] tracking-wide text-slate-300 px-1 py-0.5 truncate">{it.kind === "ai" ? "AI" : "DSN"} · {it.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <ShareDialog open={share.open} onClose={() => setShare({ open: false, dataUrl: null, url: null })}
        shareUrl={share.url} dataUrl={share.dataUrl} caption="My custom AETHER apparel" />

      <ImageEditor open={editor.open} src={editor.src} onApply={onEditApply}
        onClose={() => setEditor({ open: false, src: null })} />
    </div>
  );
}

function Panel({ title, icon: Icon, children }) {
  return (
    <div className="glass clip-chamfer p-4">
      <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-cyan/70 mb-3">
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </div>
      {children}
    </div>
  );
}
